import { useMemo } from "react";
import moment from "moment";
import usePermissions from "hooks/usePermissions";
import { IQuotation, ITrafficLightBucket } from "pages/shipper/ShipmentQuotes/components/quotes/QuotesType";

const FORMAT = "YYYY-MM-DD HH:mm:ss";

export function summarizeQuotations(quotations: any[]) {
  const result = (quotations || []).reduce(
    (acc, item) => {
      const ref = item?.reference;

      // --- delivery date ---
      const deliveryRaw = item?.schedule?.quoted_time_delivery_date;
      const delivery = deliveryRaw ? moment(deliveryRaw) : null;

      if (delivery && delivery.isValid()) {
        if (!acc.earliest || delivery.isBefore(acc.earliest.value)) acc.earliest = { ref, value: delivery };
        if (!acc.latest || delivery.isAfter(acc.latest.value)) acc.latest = { ref, value: delivery };
      }

      // --- transit time ---
      const pickupRaw = item?.schedule?.quoted_pickup_date;
      const pickup = pickupRaw ? moment(pickupRaw) : null;

      if (pickup && pickup.isValid() && delivery && delivery.isValid()) {
        const diffDays = delivery.diff(pickup, "days", true);
        if (!acc.fastest || diffDays < acc.fastest.value) acc.fastest = { ref, value: diffDays };
        if (!acc.slowest || diffDays > acc.slowest.value) acc.slowest = { ref, value: diffDays };
      }

      const cost = Number(item?.financial?.total_quoted_amount);
      const costNum = typeof cost === "number" && !Number.isNaN(cost) ? cost : null;
      if (costNum != null) {
        if (!acc.cheapest || costNum < acc.cheapest.value) acc.cheapest = { ref, value: costNum };
        if (!acc.highest || costNum > acc.highest.value) acc.highest = { ref, value: costNum };
      }

      return acc;
    },
    { earliest: null, latest: null, fastest: null, slowest: null, cheapest: null, highest: null },
  );

  const fmtDate = (m: moment.Moment) => m?.format(FORMAT);
  const fmtDays = (d: number | null) => (d == null ? null : `${d}d`);

  return {
    earliest: result.earliest ? { reference: result.earliest.ref, value: fmtDate(result.earliest.value) } : null,
    fastest: result.fastest ? { reference: result.fastest.ref, value: fmtDays(result.fastest.value) } : null,
    cheapest: result.cheapest ? { reference: result.cheapest.ref, value: result.cheapest.value } : null,
    latest: result.latest ? { reference: result.latest.ref, value: fmtDate(result.latest.value) } : null,
    slowest: result.slowest ? { reference: result.slowest.ref, value: fmtDays(result.slowest.value) } : null,
    highestCost: result.highest ? { reference: result.highest.ref, value: result.highest.value } : null,
  };
}

function sortQuotationsByTotalQuotedAmount(a: any, b: any) {
  const amountA = Number(a?.financial?.total_quoted_amount) || 0;
  const amountB = Number(b?.financial?.total_quoted_amount) || 0;
  return amountA - amountB;
}

export function useGetQuotationConsts(quotations: any[]) {
  const hasOneQuoteInApprovalPending = quotations.some((quote: IQuotation) => quote.status === "require_approval");
  const showAlternativeQuotesButton = hasOneQuoteInApprovalPending && quotations.length > 1;

  const defaultQuotations = useMemo(() => {
    if (quotations.length === 0) return [];
    if (showAlternativeQuotesButton && quotations?.length > 1) {
      return quotations
        .filter((quote: IQuotation) => quote.status === "require_approval")
        .sort(sortQuotationsByTotalQuotedAmount);
    }
    return quotations.sort(sortQuotationsByTotalQuotedAmount);
  }, [quotations, showAlternativeQuotesButton]);

  const quotationsRequireApproval = quotations
    .filter((quote: any) => quote.status !== "require_approval")
    .sort(sortQuotationsByTotalQuotedAmount);
  const hasAtLeastOneRateCardQuote = quotations.some((quote: any) => quote.general?.is_rate_card_quote);

  const quoteTags = useMemo(() => summarizeQuotations(quotations), [quotations]);

  const { permissions } = usePermissions();
  const { traffic_light_detail } = JSON.parse(permissions || "{}")?.company_shipper_settings || {};
  const { traffic_light_enabled, traffic_light_margin, traffic_light_freight_modes } = traffic_light_detail ?? {};

  const trafficLightBuckets = traffic_light_enabled
    ? getTrafficLightBuckets({
        quotations,
        traffic_light_margin,
        traffic_light_freight_modes,
      })
    : null;

  return {
    showAlternativeQuotesButton,
    defaultQuotations,
    quotationsRequireApproval,
    hasAtLeastOneRateCardQuote,
    quoteTags,
    trafficLightBuckets,
  };
}

function getTrafficLightBuckets({
  quotations,
  traffic_light_margin,
  traffic_light_freight_modes,
}: {
  quotations: any[];
  traffic_light_margin: number;
  traffic_light_freight_modes: string[];
}): ITrafficLightBucket {
  const output: ITrafficLightBucket = {};

  quotations.forEach((quote: IQuotation) => {
    const {
      service: { freight_mode_label },
      financial: { total_quoted_amount_usd },
      status,
    } = quote;
    if (!traffic_light_freight_modes.includes(freight_mode_label)) return;
    if (["canceled", "canceled_by_provider"].includes(status)) return;

    output[freight_mode_label] = {
      totalCostUSD: (output[freight_mode_label]?.totalCostUSD ?? 0) + Number(total_quoted_amount_usd),
      totalQuotes: (output[freight_mode_label]?.totalQuotes ?? 0) + 1,
    };
  });
  /* eslint-disable array-callback-return */
  Object.entries(output).map(([freightMode, data]) => {
    const { totalCostUSD, totalQuotes } = data;
    const averageCostUSD = totalCostUSD / totalQuotes;
    const upperMarginUSD = averageCostUSD + averageCostUSD * (traffic_light_margin / 100);
    const lowerMarginUSD = averageCostUSD - averageCostUSD * (traffic_light_margin / 100);
    output[freightMode] = {
      ...output[freightMode],
      averageCostUSD,
      upperMarginUSD,
      lowerMarginUSD,
    };
  });
  return output;
}

export function calculateTimeBetweenDates(date1: string, date2: string) {
  const start = moment(date1, "YYYY-MM-DD HH:mm:ss");
  const end = moment(date2, "YYYY-MM-DD HH:mm:ss");
  const diff = moment.duration(end.diff(start));
  return diff;
}

export function formatDurationFromSeconds(seconds: number) {
  const diff = moment.duration(seconds, "seconds");
  return formatDuration(diff);
}

export function formatDuration(duration: moment.Duration) {
  let diff = moment.duration(duration);

  const months = Math.floor(diff.asMonths());
  diff = diff.subtract(moment.duration(months, "months"));

  const days = Math.floor(diff.asDays());
  diff = diff.subtract(moment.duration(days, "days"));

  const hours = Math.floor(diff.asHours());
  diff = diff.subtract(moment.duration(hours, "hours"));

  const minutes = Math.floor(diff.asMinutes());

  const parts = [];
  if (months) parts.push(`${months}mo`);
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);

  return parts.slice(0, 2).join(" ");
}
